import { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../card";
import { Button } from "../button";
import { Input } from "../input";
import Badge from "../badge";
import { Avatar, AvatarImage, AvatarFallback } from "../avatar.tsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../dialog.tsx";
import {
  ChevronRight,
  Globe2,
  ShieldCheck,
  CreditCard,
  Bell,
  Palette,
  Users,
  Link2,
  Store,
  Database,
} from "lucide-react";
import { Toggle } from "../toggle.tsx";

// Forventet oppførsel:
// - Brukeren kan se og redigere DNS-postene for domenet
// - "Sjekk DNS" simulerer en validering (i produksjon bør denne kalle et API/backend)
// - Statuschip viser om DNS er OK eller mangler
// - En egen modal forklarer hvordan man setter opp domenet hos domeneleverandør
// - Abonnement-kort viser de siste fakturaene med sortering, paginering og detalj-modal

function ManualChannelsManager({ onSave }: { onSave: (channels: string[]) => void }) {
  const [channels, setChannels] = useState<string[]>([""]);

  const update = (i: number, v: string) => {
    setChannels((prev) => prev.map((c, idx) => (idx === i ? v : c)));
  };

  const add = () => setChannels((p) => [...p, ""]);

  const remove = (i: number) => {
    setChannels((p) => p.filter((_, idx) => idx !== i));
  };

  const handleSave = () => {
    const cleaned = channels.map((c) => c.trim()).filter(Boolean);
    onSave(cleaned);
  };

  return (
    <div>
      <div className="mt-3 space-y-2">
        {channels.map((ch, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              placeholder="Lim inn URL til kanal"
              value={ch}
              onChange={(e) => update(i, e.target.value)}
              className="h-8 text-xs"
            />
            <Button variant="outline" onClick={add} className="h-8 px-2">
              +
            </Button>
            {channels.length > 1 && (
              <Button variant="outline" onClick={() => remove(i)} className="h-8 px-2">
                -
              </Button>
            )}
          </div>
        ))}
      </div>
      <Button className="mt-3 w-full" onClick={handleSave}>
        Lagre kanaler
      </Button>
    </div>
  );
}

export default function InnstillingerModule() {
  type DnsRecord = { host: string; type: string; value: string };
  type DnsStatus = "idle" | "checking" | "ok" | "error";

  const [dnsRecords, setDnsRecords] = useState<DnsRecord[]>([
    { host: "@", type: "A", value: "123.45.67.89" },
    { host: "www", type: "A", value: "123.45.67.89" },
  ]);

  const [dnsStatus, setDnsStatus] = useState<DnsStatus>("idle");
  const [isDomainModalOpen, setIsDomainModalOpen] = useState(false);
  const [manualChannels, setManualChannels] = useState<string[]>([]);

  // Faktura-data – kan senere hentes fra API
  const invoices = [
    { id: "INV-2025-006", dueDate: "01.10.2025", status: "Betalt", amount: "3 990,-" },
    { id: "INV-2025-005", dueDate: "01.09.2025", status: "Betalt", amount: "3 990,-" },
    { id: "INV-2025-004", dueDate: "01.08.2025", status: "Betalt", amount: "3 990,-" },
    { id: "INV-2025-003", dueDate: "01.07.2025", status: "Forfalt", amount: "3 990,-" },
    { id: "INV-2025-002", dueDate: "01.06.2025", status: "Betalt", amount: "3 990,-" },
    { id: "INV-2025-001", dueDate: "01.05.2025", status: "Forfalt", amount: "3 990,-" },
  ];

  const [invoiceSort, setInvoiceSort] = useState<{
    field: "dueDate" | "amount" | "status";
    direction: "asc" | "desc";
  }>({
    field: "dueDate",
    direction: "desc",
  });

  const [invoicePage, setInvoicePage] = useState(0);
  const pageSize = 5;
  const [selectedInvoice, setSelectedInvoice] = useState<(typeof invoices)[number] | null>(null);

  const handleDnsChange = (index: number, field: keyof DnsRecord, value: string) => {
    setDnsRecords((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setDnsStatus("idle");
  };

  const handleValidateDns = () => {
    setDnsStatus("checking");

    const allFilled = dnsRecords.every((r) => r.host.trim() && r.type.trim() && r.value.trim());

    setTimeout(() => {
      setDnsStatus(allFilled ? "ok" : "error");
    }, 700);
  };

  const handleInvoiceSort = (field: "dueDate" | "amount" | "status") => {
    setInvoiceSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === "desc" ? "asc" : "desc",
    }));
    setInvoicePage(0);
  };

  const sortedInvoices = [...invoices].sort((a, b) => {
    switch (invoiceSort.field) {
      case "amount": {
        const toNumber = (val: string) =>
          Number(val.replace(/\s/g, "").replace(",", ".").replace(/[^0-9.]/g, ""));
        const av = toNumber(a.amount);
        const bv = toNumber(b.amount);
        return invoiceSort.direction === "asc" ? av - bv : bv - av;
      }
      case "status": {
        const order: Record<string, number> = { Betalt: 1, Forfalt: 2, Refundert: 3 };
        const av = order[a.status] ?? 99;
        const bv = order[b.status] ?? 99;
        return invoiceSort.direction === "asc" ? av - bv : bv - av;
      }
      case "dueDate":
      default: {
        const [da, ma, ya] = a.dueDate.split(".").map(Number);
        const [db, mb, yb] = b.dueDate.split(".").map(Number);
        const av = new Date(ya, ma - 1, da).getTime();
        const bv = new Date(yb, mb - 1, db).getTime();
        return invoiceSort.direction === "asc" ? av - bv : bv - av;
      }
    }
  });

  const totalInvoicePages = Math.ceil(sortedInvoices.length / pageSize);
  const currentInvoices = sortedInvoices.slice(
    invoicePage * pageSize,
    invoicePage * pageSize + pageSize,
  );

  return (
    <div className="flex h-full w-full flex-col bg-slate-50">
      {/* Top header */}
      <header className="flex items-center justify-between border-b bg-white px-8 py-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Innstillinger</h1>
          <p className="mt-1 text-sm text-slate-500">
            Administrer bedrift, domene, integrasjoner, sikkerhet og varslinger.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline">Avbryt</Button>
          <Button className="px-6">Lagre endringer</Button>
        </div>
      </header>

      {/* Content area */}
      <main className="flex-1 overflow-y-auto px-8 pb-10 pt-6">
        <div className="mx-auto flex max-w-6xl gap-6">
          {/* Left side: main settings content */}
          <div className="flex-1 space-y-6">
            {/* Bedriftsinformasjon */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-semibold">Bedriftsinformasjon</CardTitle>
                <CardDescription>
                  Grunnleggende detaljer brukt på faktura, domene og kundekommunikasjon.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <label htmlFor="company">Bedriftsnavn</label>
                    <Input id="company" placeholder="F.eks. Teletorget AS" />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="org">Organisasjonsnummer</label>
                    <Input id="org" placeholder="F.eks. 912 345 678" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="address">Adresse</label>
                  <Input id="address" placeholder="Gate, nummer, postnr og sted" />
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <label htmlFor="contact">Kontaktperson</label>
                    <Input id="contact" placeholder="Navn" />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="phone">Telefon</label>
                    <Input id="phone" placeholder="F.eks. +47 99 00 00 00" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label htmlFor="email">E-post</label>
                  <Input id="email" type="email" placeholder="support@dinbedrift.no" />
                </div>
              </CardContent>
            </Card>

            {/* Domene & abonnement */}
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-base font-semibold">
                        <Globe2 className="h-4 w-4 text-slate-400" />
                        Domene &amp; SSL
                      </CardTitle>
                      <CardDescription>Administrer primærdomene og sikker tilkobling.</CardDescription>
                    </div>
                    <span className="inline-flex items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border px-1.5 py-0 text-[10px] font-medium border-emerald-200 bg-emerald-50 text-emerald-700">SSL aktivert</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-1.5">
                    <label htmlFor="domain">Primært domene</label>
                    <div className="flex items-center gap-2">
                      <Input id="domain" placeholder="www.dinbedrift.no" className="flex-1" />
                      <Button variant="outline">
                        Ditt domene
                      </Button>
                    </div>
                  </div>
                  <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                    <div className="flex items-start justify-between gap-3">
                      <p className="max-w-xs">
                        DNS-peking håndteres via din domeneleverandør. Trenger du hjelp, kontakt oss – vi setter det
                        opp for deg.
                      </p>
                      <Button
                        variant="outline"
                        type="button"
                        onClick={() => setIsDomainModalOpen(true)}
                      >
                        Domene-veiledning
                      </Button>
                    </div>
                    <details className="mt-3 rounded-md bg-white p-3 shadow-sm">
                      <summary className="cursor-pointer text-slate-700">Rediger DNS-innstillinger</summary>
                      <div className="mt-3 space-y-2 text-[11px] text-slate-600">
                        <div className="grid grid-cols-3 items-center gap-2 border-b pb-2 text-[11px] font-medium text-slate-500">
                          <span>Host</span>
                          <span>Type</span>
                          <span>Verdi</span>
                        </div>
                        {dnsRecords.map((record, index) => (
                          <div key={`${record.host}-${index}`} className="grid grid-cols-3 items-center gap-2 py-1">
                            <Input
                              value={record.host}
                              onChange={(e) => handleDnsChange(index, "host", e.target.value)}
                              className="h-7 rounded-md bg-slate-50 px-2 text-[11px]"
                            />
                            <Input
                              value={record.type}
                              onChange={(e) => handleDnsChange(index, "type", e.target.value)}
                              className="h-7 rounded-md bg-slate-50 px-2 text-[11px]"
                            />
                            <Input
                              value={record.value}
                              onChange={(e) => handleDnsChange(index, "value", e.target.value)}
                              className="h-7 rounded-md bg-slate-50 px-2 text-[11px]"
                            />
                          </div>
                        ))}
                        <div className="mt-3 flex items-center justify-between gap-3">
                          <Button
                            variant="outline"
                            type="button"
                            onClick={handleValidateDns}
                            className="h-7 px-3 text-[11px]"
                          >
                            {dnsStatus === "checking" ? "Sjekker..." : "Sjekk DNS"}
                          </Button>
                          {dnsStatus !== "idle" && (
                            <div className="flex items-center gap-2 text-[11px]">
                              <span
                                className={
                                  "inline-flex items-center rounded-full px-2 py-0.5 " +
                                  (dnsStatus === "ok"
                                    ? "bg-emerald-50 text-emerald-700"
                                    : "bg-amber-50 text-amber-700")
                                }
                              >
                                {dnsStatus === "ok"
                                  ? "DNS OK – vi finner domenet ditt"
                                  : "Mangler / feil DNS – sjekk verdiene"}
                              </span>
                            </div>
                          )}
                        </div>
                        <p className="pt-2 text-[11px] text-slate-500">
                          Legg inn eller del disse verdiene hos din domeneforhandler. Endringer kan ta opptil 24 timer
                          før de slår inn.
                        </p>
                      </div>
                    </details>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <CardTitle className="flex items-center gap-2 text-base font-semibold">
                        <CreditCard className="h-4 w-4 text-slate-400" />
                        Abonnement &amp; fakturering
                      </CardTitle>
                      <CardDescription>Se plan, fakturahistorikk og oppgraderingsmuligheter.</CardDescription>
                    </div>
                    <span className="inline-flex items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border px-1.5 py-0 text-[9px] font-medium border-slate-200 bg-white text-slate-500">Pro Website - Inntill 10 agenter</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span>Neste fornyelse</span>
                    <span className="font-medium text-slate-900">01.10.2025</span>
                  </div>
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span>Betalingsmetode</span>
                    <span className="font-medium text-slate-900">Visa **** 4242</span>
                  </div>

                  <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                    <table className="w-full text-left text-[11px]">
                      <thead>
                        <tr className="border-b text-slate-500">
                          <th className="pb-1 font-medium">Faktura</th>
                          <th
                            className="cursor-pointer pb-1 font-medium"
                            onClick={() => handleInvoiceSort("dueDate")}
                          >
                            Forfall
                          </th>
                          <th
                            className="cursor-pointer pb-1 font-medium"
                            onClick={() => handleInvoiceSort("status")}
                          >
                            Status
                          </th>
                          <th
                            className="cursor-pointer pb-1 font-medium text-right"
                            onClick={() => handleInvoiceSort("amount")}
                          >
                            Beløp
                          </th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-700">
                        {currentInvoices.map((inv) => (
                          <tr
                            key={inv.id}
                            className="cursor-pointer border-b last:border-0 hover:bg-slate-100"
                            onClick={() => setSelectedInvoice(inv)}
                          >
                            <td className="py-1">{inv.id}</td>
                            <td className="py-1">{inv.dueDate}</td>
                            <td className="py-1">
                              <span
                                className={
                                  "inline-flex rounded-full px-2 py-0.5 " +
                                  (inv.status === "Betalt"
                                    ? "bg-emerald-50 text-emerald-700"
                                    : inv.status === "Forfalt"
                                    ? "bg-rose-50 text-rose-700"
                                    : "bg-amber-50 text-amber-700")
                                }
                              >
                                {inv.status}
                              </span>
                            </td>
                            <td className="py-1 text-right">{inv.amount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
                      <span>
                        Viser {currentInvoices.length} av {invoices.length} fakturaer
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          disabled={invoicePage === 0}
                          onClick={() => setInvoicePage((p) => Math.max(0, p - 1))}
                        >
                          Forrige
                        </Button>
                        <span>
                          Side {invoicePage + 1} av {totalInvoicePages}
                        </span>
                        <Button
                          variant="outline"
                          disabled={invoicePage >= totalInvoicePages - 1}
                          onClick={() =>
                            setInvoicePage((p) => (p + 1 >= totalInvoicePages ? p : p + 1))
                          }
                        >
                          Neste
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 pt-2">
                    <Button variant="outline">
                      Fakturahistorikk
                    </Button>
                    <Button className="ml-auto flex items-center gap-1">
                      Oppgrader plan
                      <ChevronRight className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Betalingsløsninger & integrasjoner */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-semibold">Betalingsløsninger</CardTitle>
                <CardDescription>Koble til og administrer betalingsleverandører.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                  <IntegrationTile
                    name="Stripe"
                    status="Tilkoblet"
                    description="Kortbetaling og abonnement."
                  />
                  <IntegrationTile
                    name="Vipps"
                    status="Ikke tilkoblet"
                    description="Vipps på nett og checkout."
                  />
                  <IntegrationTile
                    name="PayPal"
                    status="Ikke tilkoblet"
                    description="Betaling via PayPal-konto."
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-base font-semibold">Tekniske integrasjoner</CardTitle>
                <CardDescription>Samle analyse, sporing og automatisering på ett sted.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-4">
                  <IntegrationTile name="GA4" status="Tilkoblet" />
                  <IntegrationTile name="GTM" status="Tilkoblet" />
                  <IntegrationTile name="Meta Pixel" status="Tilkoblet" />
                  <IntegrationTile name="Zapier" status="Ikke tilkoblet" />
                </div>
              </CardContent>
            </Card>

            {/* Sikkerhet & varslinger */}
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <ShieldCheck className="h-4 w-4 text-slate-400" />
                    Sikkerhet &amp; GDPR
                  </CardTitle>
                  <CardDescription>Beskytt konto og kundedata.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-900">Tofaktorautentisering</p>
                      <p className="text-xs text-slate-500">Legg til et ekstra sikkerhetsnivå ved innlogging.</p>
                    </div>
                    <Toggle label={""} checked={false} onChange={() => console.log()} bordering={false} insideLabel="OnOffSwitch" />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-900">Automatiske sikkerhetskopier</p>
                      <p className="text-xs text-slate-500">Daglige backuper av nettsiden din.</p>
                    </div>
                    <Toggle label={""} checked={true} onChange={() => console.log()} bordering={false} insideLabel="OnOffSwitch" />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-900">Cookie- og personverninnstillinger</p>
                      <p className="text-xs text-slate-500">Tilpass samtykkeløsning og personverntekst.</p>
                    </div>
                    <Button variant="outline">
                      Administrer
                    </Button>
                  </div>
                  <div className="flex gap-3 pt-1">
                    <Button variant="outline">
                      Eksporter data
                    </Button>
                    <Button variant="outline">
                      Slett data på forespørsel
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-base font-semibold">
                    <Bell className="h-4 w-4 text-slate-400" />
                    Varslinger
                  </CardTitle>
                  <CardDescription>Velg hva du vil varsles om og hvordan.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <NotificationRow
                    title="Nye bestillinger / kjøp"
                    description="Send e-post når noen gjennomfører et kjøp eller kursbooking."
                    defaultChecked
                  />
                  <NotificationRow
                    title="Skjemainnsendinger"
                    description="Varsling når noen sender inn kontaktskjema på nettsiden."
                    defaultChecked
                  />
                  <NotificationRow
                    title="Systemoppdateringer"
                    description="Viktige meldinger om drift, oppgraderinger og sikkerhet."
                    defaultChecked
                  />
                  <NotificationRow
                    title="SMS-varsler"
                    description="Krever aktivert SMS-modul og egen avtale."
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right side: context / brukere / hurtiginformasjon */}
          <aside className="hidden w-72 flex-shrink-0 space-y-6 lg:block">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <Users className="h-4 w-4 text-slate-400" />
                  Brukere &amp; roller
                </CardTitle>
                <CardDescription>Administrer admin tilgang.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <UserRow name="John Doe" role="Admin" status="Aktiv" avatar="" />
                  <UserRow name="Victor Norman" role="Redaktør" status="Aktiv" avatar="" />
                  <UserRow name="Victoria Johnsen" role="Markedsføring" status="Invitert" avatar="" />
                </div>
                <Button variant="outline" className="w-full">
                  Legg til admin bruker
                </Button>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <Link2 className="h-4 w-4 text-slate-400" />
                  Sosiale medier
                </CardTitle>
                <CardDescription>Oversikt over publisering og kanaler.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[13px] font-medium text-slate-900">Blaze.ai</p>
                      <p className="text-[11px] text-slate-500">Automatisk publisering til sosiale medier.</p>
                    </div>
                    <span className="inline-flex items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border px-1.5 py-0 text-[10px] font-medium border-emerald-200 bg-emerald-50 text-emerald-700">Aktivt abonnement</span>
                  </div>
                </div>
                <div className="space-y-2 text-xs text-slate-600">
                  <div className="flex items-center justify-between">
                    <span>Facebook</span>
                    <span className="inline-flex items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border px-1.5 py-0 text-[10px] font-medium border-emerald-200 bg-emerald-50 text-emerald-700">Tilkoblet via Blaze</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Instagram</span>
                    <span className="inline-flex items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border px-1.5 py-0 text-[10px] font-medium border-emerald-200 bg-emerald-50 text-emerald-700">Tilkoblet via Blaze</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>TikTok</span>
                    <span className="inline-flex items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border px-1.5 py-0 text-[10px] font-medium border-slate-200 bg-white text-slate-500">Ikke tilkoblet</span>
                  </div>
                </div>

                {manualChannels.length > 0 && (
                  <div className="space-y-1.5 text-xs text-slate-600">
                    <p className="text-[11px] font-semibold text-slate-700">Manuelt tilkoblede kanaler</p>
                    {manualChannels.map((url, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span className="truncate text-[11px]" title={url}>
                          {url}
                        </span>
                        <Badge
                          variant="outline"
                          className="border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700"
                        >
                          Tilkoblet manuelt
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <Button className="w-full">
                    Gå til SOME i dashboardet
                  </Button>
                  <details className="w-full rounded-md border border-slate-200 bg-white p-2 text-xs shadow-sm">
                    <summary className="cursor-pointer text-slate-700">
                      Administrer kanaler manuelt
                    </summary>
                    <ManualChannelsManager
                      onSave={(chs) => {
                        setManualChannels(chs);
                      }}
                    />
                  </details>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <Palette className="h-4 w-4 text-slate-400" />
                  Nettsted &amp; design
                </CardTitle>
                <CardDescription>Hurtigoversikt over profilering.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span>Primærfarge</span>
                  <span className="inline-flex h-5 w-10 items-center justify-center rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 text-[10px] font-medium text-white">
                    Brand
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Font</span>
                  <span className="font-medium text-slate-900">Inter</span>
                </div>
                <Button variant="outline" className="w-full">
                  Åpne designinnstillinger
                </Button>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <Store className="h-4 w-4 text-slate-400" />
                  Moduler &amp; funksjoner
                </CardTitle>
                <CardDescription>Aktiver ekstra funksjonalitet.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-xs">
                <ModuleRow name="Blogg" enabled />
                <ModuleRow name="Kurs & events" enabled />
                <ModuleRow name="Nettbutikk" />
                <ModuleRow name="AI-innholdsassistent" />
                <Button variant="outline" className="mt-1 w-full">
                  Se alle moduler
                </Button>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <Database className="h-4 w-4 text-slate-400" />
                  Siste sikkerhetskopier
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-xs text-slate-600">
                <p>07.09.2025 – 03:12 • Automatisk</p>
                <p>06.09.2025 – 03:13 • Automatisk</p>
                <p>05.09.2025 – 15:22 • Manuell</p>
                <Button variant="outline" className="mt-2 w-full text-sm">
                  Åpne backup-oversikt
                </Button>
              </CardContent>
            </Card>
          </aside>
        </div>
      </main>

      <DomainHelpDialog open={isDomainModalOpen} onOpenChange={setIsDomainModalOpen} />
      <InvoiceDetailsDialog invoice={selectedInvoice} onClose={() => setSelectedInvoice(null)} />
    </div>
  );
}

interface IntegrationTileProps {
  name: string;
  status: string;
  description?: string;
}

function IntegrationTile({ name, status, description }: IntegrationTileProps) {
  const isConnected = status === "Tilkoblet";

  return (
    <div className="flex flex-col justify-between rounded-xl border border-slate-100 bg-slate-50/60 p-3 text-xs shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-900">
          <Link2 className="h-3.5 w-3.5 text-slate-400" />
          {name}
        </div>
        {isConnected ? (
            <span className="inline-flex items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border px-1.5 py-0 text-[10px] font-medium border-emerald-200 bg-emerald-50 text-emerald-700">{status}</span>
        ) : (
            <span className="inline-flex items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border px-1.5 py-0 text-[10px] font-medium border-slate-200 bg-white text-slate-500">{status}</span>
        )}
        
      </div>
      {description && <p className="mb-3 text-[11px] leading-snug text-slate-500">{description}</p>}
      <div className="mt-auto flex justify-end">
        <Button variant="outline" className="h-7 px-2 text-[11px]">
          {isConnected ? "Administrer" : "Koble til"}
        </Button>
      </div>
    </div>
  );
}

interface NotificationRowProps {
  title: string;
  description: string;
  defaultChecked?: boolean;
}

function NotificationRow({ title, description, defaultChecked }: NotificationRowProps) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="font-medium text-slate-900">{title}</p>
        <p className="text-xs text-slate-500">{description}</p>
      </div>
      <Toggle label={""} checked={defaultChecked ? defaultChecked : false} onChange={() => console.log()} bordering={false} insideLabel="OnOffSwitch" />
    </div>
  );
}

interface UserRowProps {
  name: string;
  role: string;
  status: "Aktiv" | "Invitert";
  avatar: string,
}

function UserRow({ name, role, status, avatar }: UserRowProps) {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2.5">
        <Avatar className="h-8 w-8">
          {avatar ? (
            <AvatarImage src={avatar} alt={name} />
          ) : (
            <AvatarFallback className="bg-slate-100 text-[11px] font-medium text-slate-700">
              {initials}
            </AvatarFallback>
          )}
        </Avatar>
        <div className="space-y-0.5">
          <p className="text-sm font-medium leading-none text-slate-900">{name}</p>
          <p className="text-[11px] text-slate-500">{role}</p>
        </div>
      </div>
      {status === "Aktiv" ? (
        <span className="inline-flex items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border px-1.5 py-0 text-[10px] font-medium border-emerald-200 bg-emerald-50 text-emerald-700">{status}</span>
      ) : (
        <span className="inline-flex items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border px-1.5 py-0 text-[10px] font-medium border-slate-200 bg-white text-slate-500">{status}</span>
      )}
    </div>
  );
}

interface ModuleRowProps {
  name: string;
  enabled?: boolean;
}

function ModuleRow({ name, enabled }: ModuleRowProps) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[11px] font-medium text-slate-800">{name}</span>
      {enabled ? (
        <span className="inline-flex items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border px-1.5 py-0 text-[10px] font-medium border-emerald-200 bg-emerald-50 text-emerald-700">Aktive</span>
      ) : (
        <span className="inline-flex items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border px-1.5 py-0 text-[10px] font-medium border-slate-200 bg-white text-slate-500">Tilgjengelig</span>
      )}
    </div>
  );
}

interface InvoiceDetailsDialogProps {
  invoice: { id: string; dueDate: string; status: string; amount: string } | null;
  onClose: () => void;
}

function InvoiceDetailsDialog({ invoice, onClose }: InvoiceDetailsDialogProps) {
  return (
    <Dialog open={!!invoice} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle>Fakturadetaljer</DialogTitle>
          <DialogDescription>Se detaljer for valgt faktura.</DialogDescription>
        </DialogHeader>
        {invoice && (
          <div className="space-y-2 text-sm text-slate-700">
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Faktura</span>
              <span className="font-medium">{invoice.id}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Forfallsdato</span>
              <span className="font-medium">{invoice.dueDate}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Status</span>
              <span className="font-medium">{invoice.status}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Beløp</span>
              <span className="font-medium">{invoice.amount}</span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Lukk
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface DomainHelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function DomainHelpDialog({ open, onOpenChange }: DomainHelpDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle>Hjelp med domeneoppsett</DialogTitle>
          <DialogDescription>
            Slik kobler du domenet ditt mot løsningen vår. Bruk disse verdiene hos din domeneleverandør.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-slate-600">
          <p>
            Logg inn hos domeneleverandøren (f.eks. Domeneshop, One.com, ProISP eller tilsvarende) og åpne
            DNS-innstillingene for domenet ditt.
          </p>
          <div className="rounded-lg bg-slate-50 p-3 text-xs">
            <div className="grid grid-cols-3 gap-2 border-b pb-2 font-medium text-slate-500">
              <span>Host</span>
              <span>Type</span>
              <span>Verdi</span>
            </div>
            <div className="grid grid-cols-3 gap-2 py-1">
              <span>@</span>
              <span>A</span>
              <span>123.45.67.89</span>
            </div>
            <div className="grid grid-cols-3 gap-2 py-1">
              <span>www</span>
              <span>A</span>
              <span>123.45.67.89 (anbefalt)</span>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Etter at du har lagret endringene, kan det ta noe tid før DNS er propagert. Du kan bruke knappen "Sjekk DNS"
            i innstillingene for å se om alt er klart.
          </p>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Lukk
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
